import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Modal } from './Modal';

export interface ConfirmModalProps {
  /** 열림 여부. */
  open: boolean;
  /** 헤더 제목. */
  title: string;
  /** 본문 안내(문자열/노드). */
  body?: ReactNode;
  /** 확인 버튼 라벨. 기본 '확인'. */
  confirmLabel?: string;
  /** 취소 버튼 라벨. 기본 '취소'. */
  cancelLabel?: string;
  /** 위험(삭제 등) 변형 — 확인 버튼 빨강 + **취소를 default(자동 포커스)로** 안전 처리. */
  danger?: boolean;
  /** 처리 중 — 확인 버튼 비활성(중복 클릭 방지). */
  busy?: boolean;
  /** 오버레이 z-index 클래스. 다른 모달 위에 뜨는 확인이면 `z-[80]`. 기본 root `z-[70]`. */
  overlayZClassName?: string;
  /** 오버레이 배경 클래스. sub 확인은 겹침 알파 보정을 위해 낮출 수 있다([[Modal]] 참조). */
  overlayBgClassName?: string;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * 공용 확인 모달 — base [[Modal]] 위에 확인/취소 default 액션 규약을 얹은 것.
 *
 * default 액션 규약:
 * - 일반: **확인**이 default → 열림 시 확인 버튼 자동 포커스 → Enter/Space=확인(네이티브 버튼 동작).
 * - danger: **취소**가 default → 취소 버튼 자동 포커스 → Enter/Space=취소(안전, 파괴적 동작 오조작 방지).
 *
 * Enter/Space를 별도 key 핸들러가 아니라 "default 버튼 자동 포커스 + 네이티브 버튼 활성"으로 구현해
 * 폼 입력의 Enter를 침해하지 않는다. Esc·오버레이·✕ = 취소(onClose)는 base Modal이 담당.
 * 네이티브 window.confirm 대체 — 동기 confirm은 pending action state로 재구성하거나 [[useConfirm]] 사용.
 */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = '확인',
  cancelLabel = '취소',
  danger = false,
  busy = false,
  overlayZClassName,
  overlayBgClassName,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  // default 버튼(자동 포커스 대상): danger면 취소, 아니면 확인.
  const defaultBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      overlayZClassName={overlayZClassName}
      overlayBgClassName={overlayBgClassName}
      initialFocusRef={defaultBtnRef}
      footer={
        <>
          <button
            type="button"
            ref={danger ? defaultBtnRef : undefined}
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={danger ? undefined : defaultBtnRef}
            onClick={onConfirm}
            disabled={busy}
            className={`rounded px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {body != null &&
        (typeof body === 'string' ? (
          // 직접 `<ConfirmModal>`·ConfirmProvider 경로도 훅 경로(아래 useConfirmDialog — 이미
          // pre-line)와 같게 본문의 줄바꿈을 그린다(두 경로가 같은 문자열을 다르게 그리지 않게).
          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600">{body}</p>
        ) : (
          body
        ))}
    </Modal>
  );
}

// ── 명령형 confirm (window.confirm 대체) ────────────────────────────────

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (result: boolean) => void;
}

/**
 * 앱(또는 서브트리) 루트에 배치. 하위 어디서든 `useConfirm()`으로 `await confirm(opts)` 가능.
 * confirm=확인, 취소/Esc/오버레이=거절(false)로 Promise resolve.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) => new Promise<boolean>((resolve) => setPending({ options, resolve })),
    [],
  );

  const settle = (result: boolean) => {
    pending?.resolve(result);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmModal
          open
          title={pending.options.title}
          body={pending.options.body}
          confirmLabel={pending.options.confirmLabel}
          cancelLabel={pending.options.cancelLabel}
          danger={pending.options.danger}
          onConfirm={() => settle(true)}
          onClose={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

/** `ConfirmProvider` 하위에서 명령형 confirm 함수를 얻는다. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm은 <ConfirmProvider> 하위에서만 사용할 수 있습니다.');
  }
  return ctx;
}

// ── Provider 없는 명령형 confirm ────────────────────────────────────────
//
// 같은 기제(대기 상태 + Promise)가 **두 형태**로 있다 — 언제 어느 것을 쓰나:
//
// | | `useConfirm` + `ConfirmProvider` | `useConfirmDialog` |
// |---|---|---|
// | 대기 상태가 사는 곳 | 루트 Provider 하나 | **그 컴포넌트 안** |
// | 배선 | 앱(또는 서브트리) 루트에 Provider 필수 — 없으면 throw | 없음. 반환된 `dialog`를 JSX에 둔다 |
// | 층(z) | Provider의 root 층 하나 | **여는 쪽이 옵션으로 주입**(모달 위면 `SUB_CONFIRM_LAYER`) |
// | 쓰는 곳(2026-10-30) | 0 | admin 전수(`window.confirm` 36자리를 옮겼다) |
//
// ⇒ **새 자리는 `useConfirmDialog`를 쓴다.** Provider 형태는 루트 배선과 「그 컴포넌트를 Provider 없이
// 렌더하는 spec 전부가 throw」를 함께 끌고 오고(admin에 Provider를 두지 않은 이유가 그것이다), 모달
// 위에서 여는 확인의 층을 정할 수 없다. 두 형태를 하나로 합치려면 Provider 쪽을 걷는 것이 맞지만 그것은
// 공용 API 삭제라 이 라운드 범위 밖이다.

/** 다른 모달(z-[70]) 위에서 여는 확인의 층 — 한 층 위 + 낮춘 배경([[Modal]]의 겹침 알파 보정 규약). */
export const SUB_CONFIRM_LAYER = {
  overlayZClassName: 'z-[80]',
  overlayBgClassName: 'bg-black/25',
} as const;

export interface ConfirmDialogOptions extends ConfirmOptions {
  /** 다른 모달 위에서 열면 [[SUB_CONFIRM_LAYER]]를 펼쳐 넣는다. 미지정이면 root(z-[70]). */
  overlayZClassName?: string;
  overlayBgClassName?: string;
}

type PendingDialog = { options: ConfirmDialogOptions; resolve: (ok: boolean) => void };

/**
 * `window.confirm` 대체 — Provider 없이, 대기 상태를 **그 컴포넌트 안**에 둔다.
 *
 * `const [confirm, confirmDialog] = useConfirmDialog();` 후 `if (await confirm({...}))`, 그리고
 * `{confirmDialog}`를 그 컴포넌트 JSX 어딘가에 둔다(포탈이라 위치는 시각과 무관하다 — 단 **React 트리**로는
 * 그 자리의 조상에게 클릭이 번지니, 클릭 핸들러가 있는 행·카드 «안»에 두지 말 것).
 *
 * 확인 = true · 취소/Esc/바깥/✕ = false. 대기 중에 다시 부르면 앞의 것은 false로 끝난다. 확인이 떠 있는
 * 채 컴포넌트가 사라지면 false로 끝난다(await가 영영 매달리지 않게). 문자열 본문의 `\n`은 줄바꿈으로
 * 보인다(`window.confirm` 문구를 그대로 옮기므로). 선례는 admin `EditorPage`의 `confirmState`다.
 */
export function useConfirmDialog(): readonly [
  (options: ConfirmDialogOptions) => Promise<boolean>,
  ReactNode,
] {
  const [pending, setPending] = useState<PendingDialog | null>(null);
  const pendingRef = useRef<PendingDialog | null>(null);
  pendingRef.current = pending;

  useEffect(
    () => () => {
      pendingRef.current?.resolve(false);
    },
    [],
  );

  const confirm = useCallback(
    (options: ConfirmDialogOptions) =>
      new Promise<boolean>((resolve) => {
        pendingRef.current?.resolve(false);
        const next = { options, resolve };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  const settle = (ok: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(ok);
  };

  const dialog = pending ? (
    <ConfirmModal
      open
      title={pending.options.title}
      body={
        typeof pending.options.body === 'string' ? (
          // `whitespace-pre-line`은 web·admin 앱 소스에 이미 쓰여 CSS에 존재한다(packages/ui 첫 사용
          // 유틸이 조용히 무스타일이 되는 축 — [[Modal]]의 `CloseGlyph` 주석).
          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600">
            {pending.options.body}
          </p>
        ) : (
          pending.options.body
        )
      }
      confirmLabel={pending.options.confirmLabel}
      cancelLabel={pending.options.cancelLabel}
      danger={pending.options.danger}
      overlayZClassName={pending.options.overlayZClassName}
      overlayBgClassName={pending.options.overlayBgClassName}
      onConfirm={() => settle(true)}
      onClose={() => settle(false)}
    />
  ) : null;

  return [confirm, dialog] as const;
}
